import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JobConfigDto, QueueNames, UpdateJobConfigDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, forkJoin, of } from 'rxjs';

@Component({
  selector: 'core-job-configs-dashboard',
  templateUrl: './job-configs-dashboard.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
})
export class JobConfigsDashboardCoreComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  configs = signal<JobConfigDto[]>([]);
  pendingCounts = signal<Record<string, number>>({});
  loading = signal<boolean>(false);
  savingQueue = signal<string | null>(null);
  error = signal<string | null>(null);

  ngOnInit(): void {
    this.loadConfigs();
  }

  loadConfigs(): void {
    this.loading.set(true);
    this.error.set(null);
    forkJoin({
      configs: this.api.admin().jobConfigs().getAllConfigs(),
      pendingCounts: this.api
        .admin()
        .jobConfigs()
        .getPendingCounts()
        .pipe(catchError(() => of({}))),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.error.set('Impossible de charger les configurations des jobs.');
          return of({ configs: [], pendingCounts: {} });
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe(({ configs, pendingCounts }) => {
        this.configs.set(configs);
        this.pendingCounts.set(pendingCounts);
      });
  }

  getQueueLabel(queueName: QueueNames): string {
    switch (queueName) {
      case QueueNames.EMAIL_SENDING:
        return "Envoi d'emails";
      case QueueNames.SMS_SENDING:
        return 'Envoi de SMS';
      default:
        return queueName;
    }
  }

  getQueueIcon(queueName: QueueNames): string {
    switch (queueName) {
      case QueueNames.EMAIL_SENDING:
        return 'mail';
      case QueueNames.SMS_SENDING:
        return 'sms';
      default:
        return 'settings';
    }
  }

  getPendingCount(queueName: QueueNames): number {
    return this.pendingCounts()[queueName] ?? 0;
  }

  togglePause(config: JobConfigDto): void {
    const updatedPaused = !config.paused;
    this.updateConfig(config.queueName, { paused: updatedPaused });
  }

  saveConfig(config: JobConfigDto): void {
    this.updateConfig(config.queueName, {
      concurrency: config.concurrency,
      maxAttempts: config.maxAttempts,
      backoffDelay: config.backoffDelay,
    });
  }

  private updateConfig(queueName: QueueNames, dto: UpdateJobConfigDto): void {
    this.savingQueue.set(queueName);
    this.api
      .admin()
      .jobConfigs()
      .updateConfig(queueName, dto)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.error.set('Erreur lors de la mise à jour de la configuration.');
          return of(null);
        }),
        finalize(() => this.savingQueue.set(null)),
      )
      .subscribe((updated) => {
        if (updated) {
          this.configs.update((list) => list.map((c) => (c.queueName === queueName ? updated : c)));
        }
      });
  }
}
